import React, { useMemo } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
  Box,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useFetchUoMs } from '../../../hooks/useFetchUoMs';
import { useGlobalItemStore } from '../../../stores/globalItemStore';
import { useStore } from 'zustand';

interface UnitOfMeasureSelectorProps {
  value: string | null;
  onChange: (value: string | null) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

const UnitOfMeasureSelector: React.FC<UnitOfMeasureSelectorProps> = ({
  value,
  onChange,
  required = false,
  error = false,
  helperText = '',
}) => {
  const theme = useTheme();
  const { currentItem } = useStore(useGlobalItemStore);
  const { data: uoms } = useFetchUoMs();

  // Memoize the safeValue computation
  const safeValue = useMemo(() => {
    if (!value || !uoms) return '';
    return uoms.some(uom => uom.id.toString() === value.toString()) ? value.toString() : '';
  }, [value, uoms]);

  // Memoize the disabled state
  const isDisabled = useMemo(() => {
    if (!currentItem) return false;
    return (
      currentItem.costsList &&
      currentItem.costsList.length > 0 &&
      currentItem.costsList[0].uomid !== 0
    );
  }, [currentItem]);

  // Memoize the menu items
  const menuItems = useMemo(() => {
    if (!uoms) return null;
    return uoms.map(uom => (
      <MenuItem key={uom.id} value={uom.id.toString()}>
        {uom.name}
      </MenuItem>
    ));
  }, [uoms]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
        <Tooltip title="Choose the unit in which the item will be measured or sold (e.g., 'Feet', 'Pieces'). Note: This cannot be changed after it is set.">
          <IconButton
            size="small"
            sx={{
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.light
                  : theme.palette.primary.main,
              mr: 1,
              mb: 1,
            }}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
        <FormControl fullWidth required={required} error={error}>
          <InputLabel
            id="uom-select-label"
            sx={{
              mt: 2,
              mb: 1,
            }}
          >
            Unit of Measure
          </InputLabel>
          <Select
            labelId="uom-select-label"
            value={safeValue}
            onChange={e => onChange(e.target.value)}
            label="Unit of Measure"
            disabled={isDisabled}
            sx={{
              mt: 2,
              mb: 2,
              '& .MuiSelect-select': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
              },
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.23)'
                    : 'rgba(0, 0, 0, 0.23)',
              },
            }}
          >
            <MenuItem value="">Select Unit of Measure</MenuItem>
            {menuItems}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default React.memo(UnitOfMeasureSelector);
